import { useIntervalFn, useNow } from "@vueuse/core";
import { computed, toValue } from "vue";
import { differenceInCalendarDays, differenceInMinutes, isValid, startOfMinute, subHours } from "date-fns";
import { TZDate } from "@date-fns/tz";
import { i18n } from "#ce/components/SpFinder/i18n";
import { formatLocalizedDate, formatLocalizedTime } from "#/shared/lib/helpers/localized-date";
import { parseOffsetDateTime } from "#ce/utils/offset-date";

// 24/7 is a numeric/symbolic label that reads the same across locales, so it
// stays a literal rather than an i18n key.
const ALWAYS_OPEN_SUMMARY = "24/7";
const CLOSING_SOON_WINDOW_HOURS = 1;
const NEXT_DAY_OFFSET = 1;
const WEEK_DAY_OFFSET = 7;
const DISTANT_DATE_OPTIONS = { month: "2-digit", day: "2-digit", year: "numeric" };

export function hasRenderableAvailability(availability = {}) {
  if (availability.open == null) {
    return false;
  }

  if (isTwentyFourSevenAvailability(availability)) {
    return true;
  }

  return (
    isValid(parseOffsetDateTime(availability.nextOpenAt)) && isValid(parseOffsetDateTime(availability.nextClosedAt))
  );
}

export function useAddressAvailability(source) {
  // Direct instance access (see SpFinder/i18n.js) — useI18n() can't resolve
  // the plugin across the custom-element app boundary. This composable is used
  // only within SpFinder, so it reads the finder catalog directly.
  const t = (...args) => i18n.global.t(...args);
  const nowDate = useNow({ scheduler: (update) => useIntervalFn(update, 60_000) });
  const availability = computed(() => toValue(source) || {});
  const parsedClosingTime = computed(() => parseOffsetDateTime(availability.value.nextClosedAt));
  const parsedOpeningTime = computed(() => parseOffsetDateTime(availability.value.nextOpenAt));
  const isTwentyFourSeven = computed(() => isTwentyFourSevenAvailability(availability.value));

  const hasAvailability = computed(() => hasRenderableAvailability(availability.value));
  const isCurrentlyOpen = computed(() => availability.value.open === true);
  const isClosingSoon = computed(() => {
    // Closing-soon only applies while open (legacy badge omitted this guard).
    if (!isCurrentlyOpen.value || isTwentyFourSeven.value) {
      return false;
    }

    const closingTime = parsedClosingTime.value.getTime();
    const windowStart = subHours(parsedClosingTime.value, CLOSING_SOON_WINDOW_HOURS).getTime();
    const now = nowDate.value.getTime();

    return now > windowStart && now <= closingTime;
  });

  const nextOpenText = computed(() => {
    if (!isValid(parsedOpeningTime.value)) {
      return t("currently_closed");
    }

    return t("opens_at", { time: formattedTime(parsedOpeningTime.value) });
  });

  const closesInText = computed(() =>
    t("closes_in_summary", { minutes: closesInMinutes(), nextOpen: nextOpenText.value }),
  );
  const nextClosesText = computed(() => {
    if (isTwentyFourSeven.value) {
      return ALWAYS_OPEN_SUMMARY;
    }

    return t("closes_at", { time: formattedTime(parsedClosingTime.value) });
  });

  const statusLabel = computed(() => {
    if (isClosingSoon.value) {
      return t("status_closing");
    }

    return isCurrentlyOpen.value ? t("status_open") : t("status_closed");
  });

  const summaryLabel = computed(() => {
    if (isClosingSoon.value) {
      return closesInText.value;
    }

    if (isCurrentlyOpen.value) {
      return nextClosesText.value;
    }

    return nextOpenText.value;
  });

  const hoursRows = computed(() => availability.value.hours || []);
  const specialHours = computed(() => availability.value.specialHours || []);
  const specialNote = computed(() => availability.value.specialNote || "");
  const note = computed(() => availability.value.note || "");
  // Empty hours can come from EHR/special-hours paths; only a missing closing time means 24/7.
  const alwaysOpenText = computed(() =>
    !hoursRows.value.length && isTwentyFourSeven.value && isCurrentlyOpen.value ? t("always_open") : "",
  );
  const hasExpandableDetails = computed(() => Boolean(hoursRows.value.length || alwaysOpenText.value));
  const specialHoursHeading = computed(() => t("special_hours"));

  function toggleLabel(expanded) {
    return `${expanded ? t("hide_hours") : t("show_hours")} ${statusLabel.value} ${summaryLabel.value}`;
  }

  function closesInMinutes() {
    return differenceInMinutes(parsedClosingTime.value, startOfMinute(nowDate.value));
  }

  // Calendar days between now and the target, both evaluated in the target's
  // own UTC offset. A plain Date carries no offset and buckets by local days.
  function calendarDayOffset(time) {
    if (!time.timeZone) {
      return differenceInCalendarDays(time, nowDate.value);
    }

    const nowInTargetOffset = new TZDate(nowDate.value.getTime(), time.timeZone);

    return differenceInCalendarDays(time, nowInTargetOffset);
  }

  function formattedTime(time) {
    if (!isValid(time)) {
      return "";
    }

    // `time` carries the address's own offset, so the badge shows the
    // location's wall-clock hours rather than the visitor's.
    const timeOptions = { minute: time.getMinutes() === 0 ? undefined : "2-digit" };
    const dayOffset = calendarDayOffset(time);

    if (dayOffset >= 0 && dayOffset <= NEXT_DAY_OFFSET) {
      return formatLocalizedTime(time, timeOptions);
    }

    // Weekday and time are formatted apart and joined, because Intl's own
    // combined pattern puts a comma between them only when the minutes are
    // omitted ("Thu, 8 AM" beside "Thu 8:00 AM").
    if (dayOffset > NEXT_DAY_OFFSET && dayOffset < WEEK_DAY_OFFSET) {
      const weekday = formatLocalizedDate(time, { weekday: "short" });

      return `${weekday} ${formatLocalizedTime(time, timeOptions)}`;
    }

    return formatLocalizedDate(time, DISTANT_DATE_OPTIONS);
  }

  return {
    hasAvailability,
    hasExpandableDetails,
    isCurrentlyOpen,
    isClosingSoon,
    statusLabel,
    summaryLabel,
    hoursRows,
    specialHours,
    specialHoursHeading,
    specialNote,
    note,
    alwaysOpenText,
    toggleLabel,
  };
}

function isTwentyFourSevenAvailability(availability) {
  return availability.open === true && availability.nextClosedAt === null;
}
